
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { StatsResult } from '@/hooks/use-studio-stats';
import { 
  ResponsiveContainer, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  Cell,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar
} from 'recharts';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';

interface StaffPerformanceProps {
  stats: StatsResult;
  isLoading: boolean;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const StaffPerformance: React.FC<StaffPerformanceProps> = ({ stats, isLoading }) => {
  // Create radar chart data
  const radarData = stats.staffPerformance.map(staff => ({
    subject: staff.name,
    bookings: staff.bookings,
    utilization: staff.utilization
  }));
  
  return (
    <div className="space-y-6">
      {/* Staff Bookings */}
      <Card>
        <CardHeader>
          <CardTitle>Mitarbeiter-Buchungen</CardTitle>
          <CardDescription>
            Verteilung der Buchungen nach Mitarbeitern
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center items-center h-60">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-chrona-accent"></div>
            </div>
          ) : stats.staffPerformance.length === 0 ? (
            <div className="flex justify-center items-center h-60">
              <p className="text-muted-foreground">Keine Daten verfügbar</p>
            </div>
          ) : (
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={stats.staffPerformance}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip
                    formatter={(value, name) => {
                      if (name === "bookings") return [`${value} Buchungen`, "Buchungen"];
                      return [`${value}%`, "Auslastung"];
                    }}
                  />
                  <Legend />
                  <Bar dataKey="bookings" fill="#2C6E49">
                    {stats.staffPerformance.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Staff Utilization */}
      <Card>
        <CardHeader>
          <CardTitle>Mitarbeiter-Auslastung</CardTitle>
          <CardDescription>
            Auslastungsrate der Mitarbeiter im ausgewählten Zeitraum
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center items-center h-40">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-chrona-accent"></div>
            </div>
          ) : stats.staffPerformance.length === 0 ? (
            <div className="flex justify-center items-center h-40">
              <p className="text-muted-foreground">Keine Daten verfügbar</p>
            </div>
          ) : (
            <div className="space-y-4">
              {stats.staffPerformance.map((staff) => (
                <div key={staff.name} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span>{staff.name}</span>
                    <span>{staff.utilization}%</span>
                  </div>
                  <Progress 
                    value={staff.utilization} 
                    className={`h-2 ${
                      staff.utilization > 80 ? "bg-green-500/20" : 
                      staff.utilization < 40 ? "bg-red-500/20" : 
                      "bg-amber-500/20"
                    }`} 
                  />
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Staff Radar Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Mitarbeiter-Performance</CardTitle>
          <CardDescription>
            Kombinierte Metriken für Mitarbeiter-Performance
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center items-center h-60">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-chrona-accent"></div>
            </div>
          ) : stats.staffPerformance.length === 0 ? (
            <div className="flex justify-center items-center h-60">
              <p className="text-muted-foreground">Keine Daten verfügbar</p>
            </div>
          ) : (
            <div className="h-[350px]">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart cx="50%" cy="50%" outerRadius="80%" data={radarData}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="subject" />
                  <PolarRadiusAxis angle={30} domain={[0, 100]} />
                  <Radar
                    name="Buchungen"
                    dataKey="bookings"
                    stroke="#0088FE"
                    fill="#0088FE"
                    fillOpacity={0.6}
                  />
                  <Radar
                    name="Auslastung (%)"
                    dataKey="utilization"
                    stroke="#00C49F"
                    fill="#00C49F"
                    fillOpacity={0.6}
                  />
                  <Legend />
                  <Tooltip />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Staff Table */}
      <Card>
        <CardHeader>
          <CardTitle>Mitarbeiter-Übersicht</CardTitle>
          <CardDescription>
            Detaillierte Performance-Daten aller Mitarbeiter
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead className="text-right">Buchungen</TableHead>
                  <TableHead className="text-right">Auslastung</TableHead>
                  <TableHead className="text-right">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stats.staffPerformance.map((staff) => (
                  <TableRow key={staff.name}>
                    <TableCell className="font-medium">{staff.name}</TableCell>
                    <TableCell className="text-right">{staff.bookings}</TableCell>
                    <TableCell className="text-right">{staff.utilization}%</TableCell>
                    <TableCell className="text-right">
                      {staff.utilization > 80 ? (
                        <span className="text-green-600">Ausgelastet</span>
                      ) : staff.utilization < 40 ? (
                        <span className="text-red-600">Unterausgelastet</span>
                      ) : (
                        <span className="text-amber-600">Normal</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StaffPerformance;
